import React, { useEffect, useState } from "react";
import { Lock, ArrowRight, Eye, EyeOff } from "lucide-react";
import ForaLogo from "./ForaLogo";

const ACCESS_CODE = "DigitalRefresh";
const STORAGE_KEY = "fora-access-granted";

export const AccessGate = ({ children }) => {
    const [unlocked, setUnlocked] = useState(false);
    const [hydrated, setHydrated] = useState(false);
    const [input, setInput] = useState("");
    const [error, setError] = useState(false);
    const [shake, setShake] = useState(false);
    const [show, setShow] = useState(false);

    useEffect(() => {
        try {
            if (localStorage.getItem(STORAGE_KEY) === "1") {
                setUnlocked(true);
            }
        } catch (e) {
            // localStorage may be unavailable (private mode); fall through
        }
        setHydrated(true);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (input.trim() === ACCESS_CODE) {
            try {
                localStorage.setItem(STORAGE_KEY, "1");
            } catch (err) {
                // ignore storage errors
            }
            setUnlocked(true);
            setError(false);
        } else {
            setError(true);
            setShake(true);
            setTimeout(() => setShake(false), 450);
        }
    };

    // Avoid flash of login page while reading localStorage
    if (!hydrated) return null;

    if (unlocked) return children;

    return (
        <div
            className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-6 py-12"
            data-testid="access-gate"
        >
            <div className="absolute inset-0 fora-pattern opacity-90" />
            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[var(--fora-red-1)] opacity-20 blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-[var(--fora-red-2)] opacity-15 blur-3xl" />

            <div
                className={`relative w-full max-w-md ${
                    shake ? "animate-shake" : ""
                }`}
            >
                <div className="flex justify-center mb-8">
                    <ForaLogo
                        variant="wordmark"
                        size="2.5rem"
                        color="white"
                    />
                </div>

                <div className="bg-black/60 backdrop-blur border border-white/15 p-8 sm:p-10">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="red-dot" />
                        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                            Confidential · Access Required
                        </span>
                    </div>

                    <h1 className="font-[800] text-3xl sm:text-4xl tracking-[-0.03em] leading-tight mb-3">
                        Enter access code.
                    </h1>
                    <p className="text-white/65 text-sm leading-relaxed mb-8">
                        Internal Progress Reports — Product &amp; IT, Business,
                        and Operations — by{" "}
                        <strong className="text-white">FORA Solutions</strong>{" "}
                        for the Master F&amp;B board. Provide the code shared
                        with you to continue.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label
                                htmlFor="code"
                                className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 block mb-2"
                            >
                                Access Code
                            </label>
                            <div className="relative">
                                <input
                                    id="code"
                                    type={show ? "text" : "password"}
                                    value={input}
                                    onChange={(e) => {
                                        setInput(e.target.value);
                                        setError(false);
                                    }}
                                    autoFocus
                                    autoComplete="off"
                                    className={`w-full bg-transparent border ${
                                        error
                                            ? "border-[var(--fora-red-1)]"
                                            : "border-white/20"
                                    } text-white text-lg pl-4 pr-12 py-3 focus:border-[var(--fora-red-1)] outline-none transition-colors tracking-[0.18em]`}
                                    placeholder="••••••••••"
                                    data-testid="access-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow((v) => !v)}
                                    className="absolute inset-y-0 right-0 px-3 flex items-center text-white/55 hover:text-white transition-colors"
                                    aria-label={show ? "Hide code" : "Show code"}
                                    data-testid="access-toggle-visibility"
                                    tabIndex={-1}
                                >
                                    {show ? (
                                        <EyeOff className="w-4 h-4" />
                                    ) : (
                                        <Eye className="w-4 h-4" />
                                    )}
                                </button>
                            </div>
                            {error && (
                                <div
                                    className="mt-2 text-xs text-[var(--fora-red-1)] font-semibold"
                                    data-testid="access-error"
                                >
                                    Invalid code. Try again or contact one of
                                    the contributors below.
                                </div>
                            )}
                        </div>

                        <button
                            type="submit"
                            className="w-full bg-[var(--fora-red-1)] hover:bg-[var(--fora-red-2)] text-white font-[700] tracking-[0.18em] uppercase text-sm px-6 py-3 transition-colors flex items-center justify-center gap-3 group"
                            data-testid="access-submit"
                        >
                            <Lock className="w-4 h-4" />
                            <span>Unlock report</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </form>
                </div>

                <p className="mt-6 text-center text-xs text-white/45 leading-relaxed">
                    Lost the code? Contact{" "}
                    <a
                        href="mailto:franjo.vucic@master-fb.com"
                        className="hover:text-white underline-offset-4 hover:underline"
                    >
                        franjo.vucic@master-fb.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default AccessGate;
